/**
 * 菜单切换时内容区淡入；地图点缀挂在淡入层外，所有菜单页共用。
 *
 * Callers: authenticated layout
 */

import { useAppPathname as usePathname } from "@/lib/navigation/use-app-pathname";
import { useLayoutEffect, useRef, type ReactNode } from "react";

import { normalizeApplicationPathname } from "../../config/normalize-application-pathname";
import { useNavigationPending } from "../feedback/navigation-pending";
import { SKELETON_FADE_MS } from "../ui/use-skeleton-crossfade";

const ENTER_DURATION_MS = SKELETON_FADE_MS;

/** 软导航：始终挂载 children；pathname 变化或 remount 时 markRouteCommitted 并淡入内容。 */
export function ShellContentTransition({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  // 只认路由键：尾斜杠矫正不算换页，避免内容区反复淡入。
  const routeKey = normalizeApplicationPathname(pathname);
  const paneRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const hasNavigatedRef = useRef(false);
  const { isSoftNavPending, markRouteCommitted } = useNavigationPending();
  const pendingAtMountRef = useRef(isSoftNavPending);

  useLayoutEffect(() => {
    const node = bodyRef.current;

    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      // Layout remount (e.g. settings shell) must settle a pending started by the click.
      if (pendingAtMountRef.current) {
        markRouteCommitted();
      }
      return;
    }

    markRouteCommitted();

    if (!node) return;
    if (typeof node.animate !== "function") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const easing =
      getComputedStyle(paneRef.current ?? node)
        .getPropertyValue("--ease-smooth")
        .trim() || "ease-out";
    const animation = node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: ENTER_DURATION_MS,
      easing,
      fill: "backwards",
    });

    return () => {
      animation.cancel();
    };
  }, [routeKey, markRouteCommitted]);

  return (
    <div className="shell-content-transition" ref={paneRef}>
      {/* 菜单页共用地图点缀，不参与内容淡入 */}
      <div
        aria-hidden
        className="shell-content-transition__decor"
        data-testid="shell-content-decor"
      />
      <div className="shell-content-transition__body" ref={bodyRef}>
        {children}
      </div>
    </div>
  );
}
